import { useAuth0 } from '@auth0/auth0-react'
import { Button, TextInput } from '@helix-ds/react'
import * as Sentry from '@sentry/react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useSearchParams } from 'react-router-dom'

import auth0Config from '../auth0/config/auth0'
import FamilyPortalAPIClient from '../FamilyPortalAPIClient'

import { SUPPORT_EMAIL, SUPPORT_PHONE } from '@/features/home/constants'
import { clientConfigLanguageForLocale } from '@/i18n/types'
import { formatMmDdYyyyInput, parseMmDdYyyyToIso } from '@/utils/formatMmDdYyyyInput'

type LoginChannel = 'email' | 'sms'
type LoginStep = 'identifier' | 'dob' | 'locked'
type LoginNextStep = 'auth0' | 'dob_required' | 'locked'

type PendingLogin = {
  channel: LoginChannel
  normalizedIdentifier: string
}

type ValidateLoginResponse = {
  nextStep?: unknown
  channel?: unknown
  normalizedIdentifier?: string
}

type VerifyLoginDobResponse = {
  outcome?: unknown
  attemptsRemaining?: number | null
  channel?: unknown
  normalizedIdentifier?: string
}

type LoginBrandingSubheadline = {
  language?: unknown
  subheadline?: unknown
}

type LoginBrandingResponse = {
  logoUrl?: unknown
  subheadlines?: unknown
}

type LoginBranding = {
  logoUrl: string | null
  subheadlines: Array<{ language: string; subheadline: string }>
}

type LoginBrandingLoadState = {
  orgId: string
  isLoading: boolean
}

const DEFAULT_LOGIN_BRANDING: LoginBranding = {
  logoUrl: null,
  subheadlines: [],
}

const SUPPORT_PHONE_DISPLAY = '(774) 229-4004'

const isLoginChannel = (channel: unknown): channel is LoginChannel => channel === 'email' || channel === 'sms'

const isLoginNextStep = (step: unknown): step is LoginNextStep =>
  step === 'auth0' || step === 'dob_required' || step === 'locked'

const isValidEmailFormat = (s: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s)

const normalizeEmailFromUrl = (s: string): string => s.trim().replace(/\s/g, '+')

function getEmailFromSearchParams(searchParams: URLSearchParams): string {
  const fromEmail = normalizeEmailFromUrl(searchParams.get('email') ?? '')
  if (fromEmail && isValidEmailFormat(fromEmail)) return fromEmail
  for (const [key, value] of searchParams.entries()) {
    const candidate = normalizeEmailFromUrl(value || key)
    if (candidate && isValidEmailFormat(candidate)) return candidate
  }
  return ''
}

function getOrgIdFromSearchParams(searchParams: URLSearchParams): string {
  return (searchParams.get('orgId') ?? '').trim()
}

function normalizeLoginBranding(data: LoginBrandingResponse): LoginBranding {
  const logoUrl = typeof data.logoUrl === 'string' && data.logoUrl.trim() ? data.logoUrl.trim() : null
  const rawSubheadlines = Array.isArray(data.subheadlines) ? data.subheadlines : []
  const subheadlines = rawSubheadlines.flatMap((item): Array<{ language: string; subheadline: string }> => {
    if (!item || typeof item !== 'object') return []
    const { language, subheadline } = item as LoginBrandingSubheadline
    if (typeof language !== 'string' || typeof subheadline !== 'string') return []
    const languageText = language.trim()
    const subheadlineText = subheadline.trim()
    if (!languageText || !subheadlineText) return []
    return [{ language: languageText, subheadline: subheadlineText }]
  })
  return { logoUrl, subheadlines }
}

function resolveLoginSubheadline(branding: LoginBranding, locale: string): string | null {
  const language = clientConfigLanguageForLocale(locale)
  const localized = branding.subheadlines.find((entry) => entry.language === language)
  const english = branding.subheadlines.find((entry) => entry.language === 'English')
  return localized?.subheadline ?? english?.subheadline ?? null
}

const Login: React.FC = () => {
  const intl = useIntl()
  const [searchParams] = useSearchParams()
  const identifierParam = getEmailFromSearchParams(searchParams)
  const orgId = getOrgIdFromSearchParams(searchParams)
  const { loginWithRedirect, isLoading } = useAuth0()
  const [step, setStep] = useState<LoginStep>('identifier')
  const [identifier, setIdentifier] = useState(() => identifierParam)
  const [pendingLogin, setPendingLogin] = useState<PendingLogin | null>(null)
  const [dob, setDob] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [loginBranding, setLoginBranding] = useState<LoginBranding>(DEFAULT_LOGIN_BRANDING)
  const [loginBrandingLoadState, setLoginBrandingLoadState] = useState<LoginBrandingLoadState>(() => ({
    orgId,
    isLoading: Boolean(orgId),
  }))
  const dobSubmitGenerationRef = useRef(0)
  const dobSubmitAbortRef = useRef<AbortController | null>(null)

  const hasOrgId = Boolean(orgId)
  const isLoginBrandingPending =
    hasOrgId && (loginBrandingLoadState.orgId !== orgId || loginBrandingLoadState.isLoading)
  const shouldUseLoginBranding = hasOrgId && loginBrandingLoadState.orgId === orgId && !loginBrandingLoadState.isLoading
  const effectiveLoginBranding = shouldUseLoginBranding ? loginBranding : DEFAULT_LOGIN_BRANDING
  const hasCustomLoginLogo = Boolean(effectiveLoginBranding.logoUrl)
  const loginLogoSrc = effectiveLoginBranding.logoUrl ?? '/icons/Inflection_Logo_Dark_LG.png'
  const loginLogoAlt = hasCustomLoginLogo
    ? intl.formatMessage({ id: 'login.healthSystemLogoAlt', defaultMessage: 'Health system logo' })
    : 'Inflection Medicine'
  const handleLoginLogoError = () =>
    setLoginBranding((current) => (current.logoUrl ? { ...current, logoUrl: null } : current))
  const customSubheadline = useMemo(
    () => resolveLoginSubheadline(effectiveLoginBranding, intl.locale),
    [effectiveLoginBranding, intl.locale]
  )

  const redirectToAuth0 = useCallback(
    async (loginChannel: LoginChannel, loginIdentifier: string) => {
      await Sentry.startSpan(
        {
          op: 'ui.click',
          name: 'FamilyPortal Login - Sign in',
        },
        async (span) => {
          span.setAttribute('auth.flow', 'family-portal')
          span.setAttribute('auth.channel', loginChannel)
          if (loginChannel === 'email') {
            span.setAttribute('email.domain', loginIdentifier.split('@')[1] || 'unknown')
          }
          const ap = auth0Config.authorizationParams
          const scope = typeof ap?.scope === 'string' ? ap.scope : 'openid profile email offline_access'
          await loginWithRedirect({
            authorizationParams: {
              redirect_uri: ap?.redirect_uri,
              response_type: 'code',
              audience: ap?.audience,
              scope,
              connection: loginChannel,
              login_hint: loginIdentifier,
              prompt: 'login',
            },
          })
        }
      )
    },
    [loginWithRedirect]
  )

  useEffect(() => {
    const fromUrl = getEmailFromSearchParams(searchParams)
    if (fromUrl && !identifier) setIdentifier(fromUrl)
  }, [searchParams, identifier])

  useEffect(() => {
    if (!orgId) {
      setLoginBranding(DEFAULT_LOGIN_BRANDING)
      setLoginBrandingLoadState({ orgId: '', isLoading: false })
      return
    }

    setLoginBranding(DEFAULT_LOGIN_BRANDING)
    setLoginBrandingLoadState({ orgId, isLoading: true })

    const controller = new AbortController()
    const loadBranding = async () => {
      try {
        const res = await FamilyPortalAPIClient.getInstance().get(
          `/config/login-branding?orgId=${encodeURIComponent(orgId)}`,
          undefined,
          false,
          controller.signal
        )
        if (controller.signal.aborted) return
        if (!res.ok) {
          setLoginBranding(DEFAULT_LOGIN_BRANDING)
          setLoginBrandingLoadState({ orgId, isLoading: false })
          return
        }
        const data = (await res.json().catch(() => ({}))) as LoginBrandingResponse
        if (!controller.signal.aborted) {
          setLoginBranding(normalizeLoginBranding(data))
          setLoginBrandingLoadState({ orgId, isLoading: false })
        }
      } catch {
        if (!controller.signal.aborted) {
          setLoginBranding(DEFAULT_LOGIN_BRANDING)
          setLoginBrandingLoadState({ orgId, isLoading: false })
        }
      }
    }

    void loadBranding()
    return () => controller.abort()
  }, [orgId])

  const handleIdentifierSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setError(null)

    const rawIdentifier = identifier.trim()
    if (!rawIdentifier) {
      setError(
        intl.formatMessage({
          id: 'login.error.identifierRequired',
          defaultMessage: 'Please enter your email address or mobile number',
        })
      )
      return
    }

    setIsSubmitting(true)
    try {
      const res = await FamilyPortalAPIClient.getInstance().post(
        '/auth/validate-login',
        { identifier: rawIdentifier },
        undefined,
        false
      )

      if (res.status === 422) {
        setError(
          intl.formatMessage({
            id: 'login.error.identifierInvalid',
            defaultMessage: 'Please enter a valid email address or mobile number',
          })
        )
        return
      }

      if (!res.ok) {
        setError(
          intl.formatMessage({
            id: 'login.error.failedToSignIn',
            defaultMessage: 'Failed to sign in. Please try again.',
          })
        )
        return
      }

      const data = (await res.json().catch(() => ({}))) as ValidateLoginResponse
      const normalizedIdentifier = (data.normalizedIdentifier || '').trim()
      if (!isLoginNextStep(data.nextStep) || !isLoginChannel(data.channel) || !normalizedIdentifier) {
        setError(
          intl.formatMessage({
            id: 'login.error.failedToSignIn',
            defaultMessage: 'Failed to sign in. Please try again.',
          })
        )
        return
      }

      const loginContext: PendingLogin = {
        channel: data.channel,
        normalizedIdentifier,
      }

      if (data.nextStep === 'locked') {
        setStep('locked')
        return
      }

      if (data.nextStep === 'auth0') {
        await redirectToAuth0(loginContext.channel, loginContext.normalizedIdentifier)
        return
      }

      setPendingLogin(loginContext)
      setDob('')
      setStep('dob')
    } catch (err) {
      Sentry.captureException(err, {
        tags: { operation: 'auth.validateLogin', flow: 'family-portal' },
      })
      setError(
        intl.formatMessage({
          id: 'login.error.failedToSignIn',
          defaultMessage: 'Failed to sign in. Please try again.',
        })
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDobSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setError(null)

    if (!pendingLogin) {
      setStep('identifier')
      return
    }

    const isoDob = parseMmDdYyyyToIso(dob)
    if (!isoDob) {
      setError(
        intl.formatMessage({
          id: 'login.dob.error.invalid',
          defaultMessage: 'Please enter a valid date of birth in MMDDYYYY format.',
        })
      )
      return
    }

    const generation = ++dobSubmitGenerationRef.current
    dobSubmitAbortRef.current?.abort()
    const controller = new AbortController()
    dobSubmitAbortRef.current = controller

    setIsSubmitting(true)
    try {
      const res = await FamilyPortalAPIClient.getInstance().post(
        '/auth/verify-login-dob',
        { identifier: pendingLogin.normalizedIdentifier, dateOfBirth: isoDob },
        undefined,
        false,
        controller.signal
      )

      if (generation !== dobSubmitGenerationRef.current) return

      if (res.status === 422) {
        setError(
          intl.formatMessage({
            id: 'login.dob.error.invalid',
            defaultMessage: 'Please enter a valid date of birth in MMDDYYYY format.',
          })
        )
        return
      }

      if (!res.ok) {
        setError(
          intl.formatMessage({
            id: 'login.error.failedToSignIn',
            defaultMessage: 'Failed to sign in. Please try again.',
          })
        )
        return
      }

      const data = (await res.json().catch(() => ({}))) as VerifyLoginDobResponse
      if (generation !== dobSubmitGenerationRef.current) return

      if (data.outcome === 'verified' && isLoginChannel(data.channel)) {
        const loginIdentifier = (data.normalizedIdentifier || pendingLogin.normalizedIdentifier).trim()
        await redirectToAuth0(data.channel, loginIdentifier)
        return
      }

      if (data.outcome === 'locked') {
        setStep('locked')
        return
      }

      const attemptsRemaining = typeof data.attemptsRemaining === 'number' ? data.attemptsRemaining : 0
      setError(
        intl.formatMessage(
          {
            id: 'login.dob.error.mismatch',
            defaultMessage:
              'That date of birth does not match our records. Please try again. You have {attemptsRemaining} attempts remaining.',
          },
          { attemptsRemaining }
        )
      )
    } catch (err) {
      if (generation !== dobSubmitGenerationRef.current) return
      if (controller.signal.aborted) return
      Sentry.captureException(err, {
        tags: { operation: 'auth.verifyLoginDob', flow: 'family-portal' },
      })
      setError(
        intl.formatMessage({
          id: 'login.error.failedToSignIn',
          defaultMessage: 'Failed to sign in. Please try again.',
        })
      )
    } finally {
      if (generation === dobSubmitGenerationRef.current) {
        setIsSubmitting(false)
        dobSubmitAbortRef.current = null
      }
    }
  }

  const handleDobChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDob(formatMmDdYyyyInput(e.target.value))
    setError(null)
  }

  const handleBackToIdentifier = () => {
    dobSubmitGenerationRef.current += 1
    dobSubmitAbortRef.current?.abort()
    dobSubmitAbortRef.current = null
    setIsSubmitting(false)
    setStep('identifier')
    setPendingLogin(null)
    setDob('')
    setError(null)
  }

  return (
    <div
      className="flex min-h-screen flex-col items-center justify-center overflow-y-auto py-8"
      style={{ backgroundColor: 'var(--color-bg-secondary)' }}
    >
      <div className="login-logo">
        {isLoginBrandingPending ? (
          <div className="login-logo__placeholder" aria-hidden="true" />
        ) : hasCustomLoginLogo ? (
          <div className="login-logo__custom-frame">
            <img
              src={loginLogoSrc}
              alt={loginLogoAlt}
              className="login-logo__custom-image"
              onError={handleLoginLogoError}
            />
          </div>
        ) : (
          <img
            src={loginLogoSrc}
            alt={loginLogoAlt}
            className="login-logo__default-image"
            onError={handleLoginLogoError}
          />
        )}
      </div>

      <div
        className="flex w-full max-w-[1024px] overflow-hidden"
        style={{
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--color-border-base)',
          boxShadow: '0px 25px 50px -12px rgba(29, 41, 61, 0.25)',
          backgroundColor: 'var(--color-bg-primary)',
        }}
      >
        <div className="relative hidden w-1/2 overflow-hidden lg:block">
          <img
            src="/images/login-hero.jpg"
            alt={intl.formatMessage({ id: 'login.heroAlt', defaultMessage: 'Login hero — newborn baby' })}
            className="absolute inset-0 h-full w-full object-cover"
          />
        </div>

        <div className="flex w-full flex-col justify-center gap-6 px-6 py-8 sm:px-10 sm:py-10 lg:w-1/2 lg:gap-8 lg:px-16 lg:py-12">
          <div className="flex flex-col gap-3">
            <h1
              className="text-4xl leading-9 font-semibold tracking-tight"
              style={{ color: 'var(--color-text-heading)' }}
            >
              <FormattedMessage id="login.heading" defaultMessage="Welcome" />
            </h1>
            <p className="text-base leading-7" style={{ color: 'var(--color-text-body)' }}>
              {isLoginBrandingPending ? (
                <span aria-hidden="true">&nbsp;</span>
              ) : (
                (customSubheadline ?? (
                  <FormattedMessage
                    id="login.subheading"
                    defaultMessage="Genomic health, explained. Your family's care all in one place."
                  />
                ))
              )}
            </p>
          </div>

          <div
            className="rounded-lg p-4"
            style={{
              backgroundColor: 'var(--color-bg-secondary)',
              border: '1px solid var(--color-border-base)',
            }}
          >
            <p className="text-sm leading-5" style={{ color: 'var(--color-success-700)' }}>
              <FormattedMessage
                id="login.secureSpaceMessage"
                defaultMessage="This is your secure space for results, answers, and support, whenever you're ready."
              />
            </p>
          </div>

          {step === 'locked' ? (
            <div
              className="rounded-xl p-4"
              style={{ backgroundColor: 'var(--color-error-50)', border: '1px solid var(--color-error-500)' }}
              role="alert"
            >
              <p className="text-sm leading-6" style={{ color: 'var(--color-error-700)' }}>
                <FormattedMessage
                  id="login.locked.message"
                  defaultMessage="For your security, access has been locked after too many incorrect attempts. Please contact our support team at {supportPhone} or {supportEmail} and we will help you get signed in."
                  values={{
                    supportPhone: (
                      <a href={`tel:${SUPPORT_PHONE}`} className="underline">
                        {SUPPORT_PHONE_DISPLAY}
                      </a>
                    ),
                    supportEmail: (
                      <a href={`mailto:${SUPPORT_EMAIL}`} className="underline">
                        {SUPPORT_EMAIL}
                      </a>
                    ),
                  }}
                />
              </p>
            </div>
          ) : step === 'dob' ? (
            <form onSubmit={handleDobSubmit} className="flex flex-col gap-2.5">
              <p className="text-sm leading-6" style={{ color: 'var(--color-text-body)' }}>
                <FormattedMessage
                  id="login.dob.prompt"
                  defaultMessage="To keep this information secure, please enter the date of birth of the person being tested. If you are also being tested, you can enter your own date of birth instead. Please enter the date of birth in the format MMDDYYYY."
                />
              </p>
              <TextInput
                id="login-dob"
                type="text"
                inputMode="numeric"
                label={intl.formatMessage({
                  id: 'login.dob.label',
                  defaultMessage: 'Date of birth',
                })}
                value={dob}
                onChange={handleDobChange}
                disabled={isSubmitting || isLoading}
                placeholder={intl.formatMessage({
                  id: 'login.dob.placeholder',
                  defaultMessage: 'MM/DD/YYYY',
                })}
                autoComplete="bday"
                required
                aria-describedby={error ? 'login-dob-error' : undefined}
              />
              {error && (
                <div
                  id="login-dob-error"
                  className="rounded-xl p-3"
                  style={{ backgroundColor: 'var(--color-error-50)', border: '1px solid var(--color-error-500)' }}
                >
                  <p className="text-sm" style={{ color: 'var(--color-error-700)' }} role="alert">
                    {error}
                  </p>
                </div>
              )}
              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                className="mt-1 min-h-12"
                disabled={isSubmitting || isLoading || !dob.trim()}
                aria-busy={isSubmitting || isLoading}
                loading={isSubmitting || isLoading}
              >
                {isSubmitting || isLoading
                  ? intl.formatMessage({ id: 'login.dob.verifying', defaultMessage: 'Verifying…' })
                  : intl.formatMessage({ id: 'login.dob.submitButton', defaultMessage: 'Continue' })}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="lg"
                fullWidth
                onClick={handleBackToIdentifier}
                disabled={isSubmitting || isLoading}
              >
                <FormattedMessage id="login.dob.backButton" defaultMessage="Back" />
              </Button>
            </form>
          ) : (
            <form onSubmit={handleIdentifierSubmit} className="flex flex-col gap-2.5">
              <TextInput
                id="identifier"
                type="text"
                label={intl.formatMessage({
                  id: 'login.form.identifierLabel',
                  defaultMessage: 'Email or mobile number',
                })}
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                disabled={isSubmitting || isLoading}
                placeholder={intl.formatMessage({
                  id: 'login.form.identifierPlaceholder',
                  defaultMessage: 'Enter your email or mobile number',
                })}
                autoComplete="username"
                required
                aria-label={intl.formatMessage({
                  id: 'login.form.identifierAriaLabel',
                  defaultMessage: 'Email address or mobile number',
                })}
                helperText={intl.formatMessage({
                  id: 'login.form.identifierHelperText',
                  defaultMessage: "We'll send a sign-in code by email or text message.",
                })}
              />

              {error && (
                <div
                  className="rounded-xl p-3"
                  style={{ backgroundColor: 'var(--color-error-50)', border: '1px solid var(--color-error-500)' }}
                >
                  <p className="text-sm" style={{ color: 'var(--color-error-700)' }} role="alert">
                    {error}
                  </p>
                </div>
              )}

              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                className="mt-1 min-h-12"
                disabled={isSubmitting || isLoading || !identifier.trim()}
                aria-label={intl.formatMessage({
                  id: 'login.form.submitAriaLabel',
                  defaultMessage: 'Continue to sign in',
                })}
                aria-busy={isSubmitting || isLoading}
                loading={isSubmitting || isLoading}
              >
                {isSubmitting || isLoading
                  ? intl.formatMessage({ id: 'login.form.sendingCode', defaultMessage: 'Sending code…' })
                  : intl.formatMessage({ id: 'login.form.submitButton', defaultMessage: 'Continue' })}
              </Button>
            </form>
          )}

          <p className="text-sm leading-5 font-medium" style={{ color: 'var(--color-text-subtle)' }}>
            <FormattedMessage
              id="login.footer.agreement"
              defaultMessage="By continuing, you agree to Inflection Medicine's {privacyPolicy} and {termsAndConditions}. Your use of this platform is governed by these terms."
              values={{
                privacyPolicy: (
                  <a
                    href="https://inflectionmedicine.com/documents/inflection-medicine-privacy-policy-jan2026.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                    style={{ color: 'var(--color-success-700)' }}
                  >
                    <FormattedMessage id="login.footer.privacyPolicy" defaultMessage="Privacy Policy" />
                  </a>
                ),
                termsAndConditions: (
                  <a
                    href="https://inflectionmedicine.com/documents/inflection-website-terms-and-conditions.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                    style={{ color: 'var(--color-success-700)' }}
                  >
                    <FormattedMessage id="login.footer.termsAndConditions" defaultMessage="Terms &amp; Conditions" />
                  </a>
                ),
              }}
            />
          </p>
        </div>
      </div>
    </div>
  )
}

export default Login
